import type { ComponentPropsWithoutRef, ElementRef } from 'react'
import { forwardRef, useEffect, useState } from 'react'
import { apiClient } from '@/lib/api/api-client'
import { profileImageCache } from '@/lib/services/profile-image-cache.service'
import { cn } from '@/lib/utils'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { getModuleColors, hslToString, ModuleTypeEnum } from '@scrambl/shared'

// Available module types for avatar colors
const AVATAR_MODULE_TYPES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.LEAD,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.EVENT,
  ModuleTypeEnum.CONTRACT,
  ModuleTypeEnum.INVOICE,
  ModuleTypeEnum.MEMBER,
]

// Function to generate consistent color from string using module color system
const getAvatarColor = (
  identifier: string = '',
  theme: 'light' | 'dark' = 'light',
) => {
  // If no identifier, use default profile colors
  if (!identifier) {
    const defaultColors = getModuleColors(ModuleTypeEnum.MEMBER, theme)
    return hslToString(defaultColors.primary)
  }

  // Generate consistent hash from identifier
  let hash = 0
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Get module type based on hash
  const index = Math.abs(hash % AVATAR_MODULE_TYPES.length)
  const moduleType = AVATAR_MODULE_TYPES[index]

  // Get color from module system
  const moduleColors = getModuleColors(moduleType, theme)
  return hslToString(moduleColors.primary)
}

const Avatar = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
      className,
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const publicAssetUrlCache = new Map<string, string | null>()
const pendingPublicAssetUrlRequests = new Map<string, Promise<string | null>>()

const isResolvableAssetKey = (src?: string): src is string => {
  return Boolean(
    src &&
    !src.startsWith('http://') &&
    !src.startsWith('https://') &&
    !src.startsWith('blob:') &&
    !src.startsWith('data:'),
  )
}

const getProfileImageUserId = (src: string): string | null => {
  const match = /^profile\/([^/]+)\//.exec(src)
  return match?.[1] ?? null
}

const getOrganizationLogoOrganizationId = (src: string): string | null => {
  const match = /^organization\/([^/]+)\//.exec(src)
  return match?.[1] ?? null
}

const getOrganizationLogoUrl = (
  organizationId: string,
  sourceKey: string,
): Promise<string | null> => {
  const cacheKey = `organization-logo:${sourceKey}`
  const cached = publicAssetUrlCache.get(cacheKey)
  if (cached !== undefined) {
    return Promise.resolve(cached)
  }

  const pending = pendingPublicAssetUrlRequests.get(cacheKey)
  if (pending) {
    return pending
  }

  const request = apiClient<{ url: string | null }>(
    `/organizations/${organizationId}/logo`,
    { skipDeduplication: true },
  )
    .then(response => {
      publicAssetUrlCache.set(cacheKey, response.url)
      return response.url
    })
    .catch(() => {
      publicAssetUrlCache.set(cacheKey, null)
      return null
    })
    .finally(() => {
      pendingPublicAssetUrlRequests.delete(cacheKey)
    })

  pendingPublicAssetUrlRequests.set(cacheKey, request)
  return request
}

const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
  const [resolvedSrc, setResolvedSrc] = useState(src)

  useEffect(() => {
    if (!isResolvableAssetKey(src)) {
      setResolvedSrc(src)
      return
    }

    const profileUserId = getProfileImageUserId(src)
    const organizationId = getOrganizationLogoOrganizationId(src)

    if (!profileUserId && !organizationId) {
      setResolvedSrc(src)
      return
    }

    let mounted = true
    setResolvedSrc(undefined)

    const resolveUrl = profileUserId
      ? profileImageCache.getProfileImage(profileUserId)
      : getOrganizationLogoUrl(organizationId as string, src)

    resolveUrl
      .then(url => {
        if (mounted) {
          setResolvedSrc(url ?? undefined)
        }
      })
      .catch(() => {
        if (mounted) {
          setResolvedSrc(undefined)
        }
      })

    return () => {
      mounted = false
    }
  }, [src])

  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={resolvedSrc}
      // `block object-cover` is defensive — preflight makes <img> block-level
      // already, but Tailwind also applies `height: auto` which can briefly
      // collapse the element before our utility classes win. object-cover
      // ensures the image fills the circle when the source isn't square.
      className={cn(
        'block aspect-square h-full w-full object-cover',
        className,
      )}
      {...props}
    />
  )
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

interface AvatarFallbackProps extends ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Fallback
> {
  identifier?: string // Optional identifier to generate consistent colors
  theme?: 'light' | 'dark' // Theme for color generation
}

const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(
  (
    { className, identifier, theme = 'light', children, style, ...props },
    ref,
  ) => {
    // Background color generation (React Compiler handles memoization)
    const fallbackText = typeof children === 'string' ? children : ''
    const idForColor = identifier || fallbackText
    const backgroundColor = getAvatarColor(idForColor, theme)

    const fallbackStyle = {
      backgroundColor,
      color: 'white', // Use consistent white text for good contrast
      ...style,
    }

    return (
      <AvatarPrimitive.Fallback
        ref={ref}
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full font-semibold text-[inherit] text-white',
          className,
        )}
        style={fallbackStyle}
        {...props}
      >
        {children}
      </AvatarPrimitive.Fallback>
    )
  },
)
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
